import {put, takeEvery, debug, createAction, delay, take, race} from '../../lib'
import {v4} from 'uuid'
import {AUTH_REFRESHING_FAILURE, AUTH_SET, REQUEST_ERROR} from '../state'
import {
  REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
  REQUEST_IDENTITY_AUTHENTICATE,
  PASSWORD_RESET_SUBMIT,
  EMAIL_UPDATE_SUBMIT,
  EMAIL_VERIFY_SUBMIT,
  doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
  REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
  REQUEST_IDENTITY_MFA_ENABLED_UPDATE,
} from '..'

export const HANDLE_EXPIRED_TOKEN = 'HANDLE_EXPIRED_TOKEN'
export const doHANDLE_EXPIRED_TOKEN = createAction(HANDLE_EXPIRED_TOKEN)

export const handleExpiredTokenActionCreators = {
  doHANDLE_EXPIRED_TOKEN,
}

// a replay that has waited this long is dropped. The clock starts at the 401, not at the
// refresh attempt, so a retry schedule that outlives the outage cannot keep it queued for hours.
export const REPLAY_DEADLINE_MS = 20000

// Requests that must never be replayed under a *new* token. The refresh itself and the
// unauthenticated flows would loop; the proof-carrying mutations (MFA toggle, email change)
// would be re-applied behind a UI that already reported the failure, and a replay stripped of
// its proof would be applied as an unproved change.
//
// Built per call, NOT once at module scope: this module and the '..' barrel are part of the
// existing lib/store init cycle, so every one of these constants is still `undefined` while this
// file is being evaluated. A list captured up there skips nothing -- including the refresh's own
// 401, which then dispatches another refresh, for ever.
const neverReplay = (fromType: string): boolean =>
  [
    REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
    PASSWORD_RESET_SUBMIT,
    REQUEST_IDENTITY_AUTHENTICATE,
    EMAIL_UPDATE_SUBMIT,
    EMAIL_VERIFY_SUBMIT,
    REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
    REQUEST_IDENTITY_MFA_ENABLED_UPDATE,
  ].includes(fromType)

export function* handleExpiredToken({payload, setRequestResult}: any) {
  debug('saga*handleExpiredToken')

  const {fromType, fromPayload, statusCode} = payload

  if (statusCode !== 401) return
  if (neverReplay(fromType)) return

  const deadlineAt = Date.now() + REPLAY_DEADLINE_MS

  const requestId = v4()
  yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({requestId}))

  // The session still has to recover, so the refresh above is dispatched either way -- but a
  // request saga that emitted no `fromPayload` cannot be replayed faithfully, and the
  // proof-carrying sagas emit none, so the rule enforces itself.
  if (!fromPayload) return

  // Only a fresh token makes the replay safe. A terminal outcome ends the session, and an
  // AUTH_REFRESH_UNAVAILABLE in between is not an outcome at all -- it leaves this waiting until
  // either the retry finally succeeds or the deadline drops it.
  const {authSet} = yield race({
    authSet: take(AUTH_SET),
    sessionEnded: take(AUTH_REFRESHING_FAILURE),
    expired: delay(Math.max(deadlineAt - Date.now(), 0)),
  })

  if (!authSet) return

  // the 401 already told the caller the request failed, so the replay has to report back to the
  // same setter or the screen keeps showing a failure for a write that landed
  yield put({
    type: fromType,
    payload: fromPayload,
    setRequestResult,
  })
}

export function* handleExpiredTokenSaga() {
  yield takeEvery(REQUEST_ERROR, handleExpiredToken)
}
